import React, { useEffect, useRef } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuChartColumn, LuLayoutDashboard, LuSettings2, LuShieldCheck, LuUsers } from 'react-icons/lu';
import UsersManagement from '../components/admin/UsersManagement';
import ConfigManagement from '../components/admin/ConfigManagement';
import PlausibleData from '../components/admin/PlausibleData';
import { ViewerMobileNav, ViewerSidebar } from '../components/ui/ViewerSidebar';
import PageHeader from '../components/ui/PageHeader';
import IconChip from '../components/ui/IconChip';
import ErrorBoundary from '../components/ui/ErrorBoundary';

const SECTIONS = [
  { id: 'dashboard', path: '/admin', label: 'Tableau de bord', icon: LuLayoutDashboard },
  { id: 'analytics', path: '/admin/analytics', label: 'Statistiques', icon: LuChartColumn, description: 'Fréquentation de Globe Weekly (Plausible) : pages vues, rapports consultés, événements.' },
  { id: 'users', path: '/admin/users', label: 'Utilisateurs', icon: LuUsers, superadmin: true, description: 'Comptes et rapports autorisés, en lecture seule : les droits se gèrent dans Globe Connect.' },
  { id: 'configs', path: '/admin/configs', label: 'Configurations', icon: LuSettings2, superadmin: true, description: 'Clients, sources de données, partage public et prompts d\'analyse.' },
];

const AdminDashboard = ({ sections }) => (
  <div className="space-y-4">
    <PageHeader title="Administration" subtitle="Suivi de l'usage et paramétrage des rapports hebdomadaires" />
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {sections.filter((s) => s.id !== 'dashboard').map((section) => (
        <Link
          key={section.id}
          to={section.path}
          className="group flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm outline-none transition-all hover:-translate-y-px hover:border-brand-200 hover:shadow-md focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-900 dark:bg-night-800 dark:hover:border-brand-800"
        >
          <IconChip icon={section.icon} className="mb-4" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">{section.label}</h2>
          <p className="mt-1 flex-1 text-sm text-gray-500 dark:text-gray-400">{section.description}</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400">
            Ouvrir
            <LuArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </span>
        </Link>
      ))}
    </div>
  </div>
);

const AdminPage = ({ fetchWithAuth, currentUser }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!currentUser?.isadmin) navigate('/');
  }, [currentUser, navigate]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const sections = SECTIONS.filter((s) => !s.superadmin || currentUser?.issuperadmin);
  const current = sections.find((s) => s.path !== '/admin' && pathname.startsWith(s.path))?.id || 'dashboard';

  const navSections = [{
    id: 'admin',
    items: sections.map((s) => ({ id: s.id, label: s.label, icon: s.icon, to: s.path, active: current === s.id })),
  }];

  const header = (
    <div className="flex items-center gap-2.5 rounded-xl border border-gray-200 px-2.5 py-2 dark:border-gray-800">
      <IconChip icon={LuShieldCheck} />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase leading-none tracking-wider text-gray-400 dark:text-gray-500">Espace</p>
        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">Administration</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-full overflow-hidden">
      <ViewerSidebar header={header} sections={navSections} navLabel="Navigation de l'administration" />
      <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:px-6 sm:py-6">
          <ViewerMobileNav sections={navSections} navLabel="Navigation de l'administration" />
          <ErrorBoundary resetKeys={[pathname]}>
            <Routes>
              <Route index element={<AdminDashboard sections={sections} />} />
              <Route path="users" element={<UsersManagement fetchWithAuth={fetchWithAuth} />} />
              <Route path="configs" element={<ConfigManagement fetchWithAuth={fetchWithAuth} />} />
              <Route path="analytics" element={<PlausibleData />} />
            </Routes>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
